/* eslint-disable import/prefer-default-export */
/* eslint-disable import/extensions */
import React, {
    useState,
    useEffect,
    useRef
} from 'react';

import {
    createRoot
} from 'react-dom/client';
import {
    flushSync
} from 'react-dom';

import RIQButton from '../../../components/RIQButton.jsx';

import RIQModal from '../../../components/RIQModal.jsx';

import RIQLoading from '../../../components/RIQLoading.jsx';

import {
    useListThemes,
    useListTopics
} from '../hooks/useQuidTopicsPersistence.js';

import {
    useListLandscapeQuidTopics,
    useSaveLandscapeListeningQuery
} from '../hooks/useListeningQueryPersistence.js';

import Selectize from '../../../components/Selectize.jsx';

import {
    listeningDefinitions
} from './enums/listeningDefinitions.jsx';

import {
    RIQInfoPopover,
    useRIQTooltip
} from '../../../components/RIQPopover.jsx';

import {
    useCurationConfigValue
} from '../../../hooks/widgetConfiguration.js';

import useValidateQuidMonitorTopic from '../hooks/useValidateQuidMonitorTopic.js';

const {
    maxTopicsPerLandscape,
    id: quidTopicTypeId
} = listeningDefinitions.listeningQueryTypes.QuidTopic;

const {
    title,
    description,
    disabledTooltipText,
    topicDescription,
    themeDescription,
    topicMetaInfo,
    themeMetaInfo
} = listeningDefinitions['configure-topic-modal'];

const MAX_THEMES = 5;

export function ConfigureTopicModal() {

    const [showCreateQuidTopicModal, setShowCreateQuidTopicModal] = useState(false);

    const {data: topics} = useListLandscapeQuidTopics();

    const {setTriggerRef: disabledTriggerRef, tooltipNode: disabledTooltipNode} = useRIQTooltip({
        text: disabledTooltipText,
        placement: 'left',
        delayShow: 200
    });

    const disabled = _.size(topics) >= maxTopicsPerLandscape;

    function onCreateQuidTopicClick() {
        setShowCreateQuidTopicModal(true);
    }

    return (
        <>
            <div className='tlw-mb-20 tlw-flex tlw-flex-col tlw-items-end'>
                <div
                    ref={disabledTriggerRef}
                >
                    <RIQButton
                        onClick={onCreateQuidTopicClick}
                        variant='primary'
                        className='tlw-px-6 tlw-py-3 tlw-text-md tlw-font-normal'
                        disabled={disabled}
                    >
                        <span className='fa fa-plus fa-fw' />
                    &nbsp;Connect Topic
                    </RIQButton>
                </div>
                {disabled && disabledTooltipNode}
            </div>

            {showCreateQuidTopicModal &&
                <Modal
                    onClose={() => setShowCreateQuidTopicModal(false)}
                />
            }
        </>
    );
}


function Modal({onClose}) {

    const timezone = useCurationConfigValue('timezone');
    const {data: topics} = useListTopics();
    const {data: themes} = useListThemes();

    const {mutate: saveQuidTopic} = useSaveLandscapeListeningQuery();

    const isLoading = !topics || !themes;

    const [selectedTopicId, setSelectedTopicId] = useState(null);
    const [selectedThemeIds, setSelectedThemeIds] = useState([]);

    const validationResult = useValidateQuidMonitorTopic(selectedTopicId, selectedThemeIds);

    const topicDropdownOptions = _.map(topics, topic => {
        return {
            id: topic.topicId,
            name: topic.name,
            selected: selectedTopicId === topic.topicId,
            disabled: topic.status !== 'Active',
            owner: topic.owner.name,
            description: topic.description,
            timeInterval: RivalIQ.utils.timePeriod.getQuidTopicTimeInterval(topic, timezone),
            statusStandby: topic.status === 'Standby'
        };
    });

    const themeDropdownOptions = _.map(themes, theme => {
        return {
            id: theme.themeId,
            name: theme.name,
            owner: theme.owner.name
        };
    });

    const {setTriggerRef: saveButtonTriggerRef, tooltipNode: saveButtonTooltipNode} = useRIQTooltip({
        text: validationResult.error ? validationResult.error.message : '',
        placement: 'top',
        delayShow: 200
    });

    // This dance is because the selectize item renderer returns a string, but we need to attach a click handler to the delete button.
    // This code creates a global function that can be called from the item renderer, then deletes it when the component unmounts.
    const onThemeDeletedCbName = useRef(_.uniqueId('onThemeDeletedCbName_'));
    useEffect(() => {

        window[onThemeDeletedCbName.current] = function (themeId) {
            setSelectedThemeIds(_.reject(selectedThemeIds, selectedThemeId => Number(selectedThemeId) === Number(themeId)));
        };

        return () => {
            // eslint-disable-next-line react-hooks/exhaustive-deps
            delete window[onThemeDeletedCbName.current];
        };
    });

    function onCreateQuidTopicClick() {

        saveQuidTopic({
            listeningQueryTypeId: quidTopicTypeId,
            definition: {
                topicId: selectedTopicId,
                themeIds: selectedThemeIds
            }
        }, {
            onSuccess: () => {
                onClose();
            },
            onError: () => {
                RivalIQ.execute('alert:danger', 'An error occurred, please try again');
            }
        });
    }

    function onTopicSelected(topicId) {
        setSelectedTopicId(topicId);
    }

    function onThemeSelected(themeIds) {
        setSelectedThemeIds(themeIds);
    }

    function topicMetaInfoBubble(option, escape) {
        let optionClasses = '';
        if (option.disabled) {
            optionClasses = 'tlw-text-gray-500 hover_!tlw-text-gray-500 tlw-bg-transparent tlw-cursor-not-allowed';
        }
        const metaInfo = topicMetaInfo(option.description, option.owner, option.timeInterval, option.statusStandby);

        return metaInfoPopover(option.name, metaInfo, escape, optionClasses);
    }

    function themeMetaInfoBubble(option, escape) {
        const metaInfo = themeMetaInfo(option.owner);
        return metaInfoPopover(option.name, metaInfo, escape);
    }

    function metaInfoPopover(optionName, metaInfo, escape, optionClasses = '') {
        const div = document.createElement('div');
        const root = createRoot(div);

        // using flushSync means that we can leave the metaInfo being complied in the listeningDefinitions.jsx
        // optionRenderer for the Selectize dropdown requires an html string
        // while react offers renderToString from react-dom/server, it is frowned upon to use on client side code
        // https://react.dev/reference/react-dom/server/renderToString#removing-rendertostring-from-the-client-code
        flushSync(() => {
            root.render(metaInfo);
        });

        return (
            `<div class='${optionClasses}'>
                ${optionName}
                <span class="fa fa-info-circle fa-color fa-fw tlw-cursor-pointer" 
                    data-title='${escape(optionName)}' data-content='${escape(div.innerHTML)}' data-toggle='popover'
                />
            </div>`
        );
    }

    return (
        <RIQModal
            title={title}
            showModal={true}
            variant='large'
            overflow='visible'
        >
            <div className='tlw-m-15 tlw-flex tlw-min-h-[180px] tlw-flex-col tlw-place-items-center tlw-gap-20'>
                {isLoading && <RIQLoading />}

                {!isLoading &&
                <>
                    <div className='tlw-text-center tlw-text-md tlw-leading-24'>
                        {description}
                    </div>
                    <div className='tlw-flex tlw-h-auto tlw-w-full tlw-flex-col tlw-gap-0 tlw-overflow-visible'>
                        <QuidTopicSelector
                            title='Topic'
                            description={topicDescription}
                        >
                            <Selectize
                                items={_.compact([selectedTopicId])}
                                onChange={onTopicSelected}
                                options={topicDropdownOptions}
                                optionRenderer={(option, escape) => {
                                    return topicMetaInfoBubble(option, escape);
                                }}
                            />
                        </QuidTopicSelector>

                        <QuidTopicSelector
                            title='Theme'
                            description={themeDescription}
                        >
                            <Selectize
                                items={selectedThemeIds}
                                inputClasses='tlw-flex tlw-flex-wrap tlw-h-auto tlw-gap-y-4 tlw-min-h-19'
                                onChange={onThemeSelected}
                                options={themeDropdownOptions}
                                optionRenderer={(option, escape) => {
                                    return themeMetaInfoBubble(option, escape);
                                }}
                                placeholder='None'
                                disabled={Boolean(!selectedTopicId)}
                                maxItems={MAX_THEMES}
                                itemRenderer={(item, escape) => {
                                    return `
                                        <div
                                            class="tlw-mr-2 tlw-flex tlw-max-w-[300px] tlw-items-center tlw-rounded-[15px] tlw-bg-gray-150 tlw-text-black tlw-py-1 tlw-pl-5 tlw-pr-2.5"
                                            >
                                            <span class="tlw-w-full tlw-overflow-hidden tlw-text-ellipsis tlw-whitespace-nowrap">
                                                ${escape(item.name)}
                                            </span>

                                            <div class="tlw-cursor-pointer tlw-pl-2">
                                                <span
                                                    class="tlw-flex tlw-h-10 tlw-w-10 tlw-items-center tlw-justify-center tlw-rounded-[50%] tlw-p-1 tlw-text-black hover_tlw-bg-gray-500 hover_tlw-text-white"
                                                    onClick="window.${onThemeDeletedCbName.current}(${item.id})"
                                                    aria-hidden="true"
                                                >
                                                    ×
                                                </span>
                                            </div>

                                        </div>`;
                                }}
                            />
                            {selectedThemeIds.length === MAX_THEMES &&
                                <div className='tlw-text-sm tlw-text-red-500'>
                                    {`Quid Monitor Topics are limited to max ${MAX_THEMES} themes.`}
                                </div>
                            }
                        </QuidTopicSelector>
                    </div>
                </>
                }
            </div>

            <div
                className='tlw-m-0 tlw-flex tlw-grow tlw-flex-row tlw-items-center tlw-border-0 tlw-border-t tlw-border-solid tlw-border-gray-300 tlw-p-7.5 tlw-text-base tlw-leading-22 tlw-text-gray-900'
            >
                <RIQButton
                    variant='default'
                    className='tlw-mx-4 tlw-ml-auto tlw-self-center tlw-px-6 tlw-py-3'
                    onClick={onClose}
                >
                    Cancel
                </RIQButton>

                <div
                    ref={saveButtonTriggerRef}
                >
                    <RIQButton
                        variant='primary'
                        className='tlw-mx-4 tlw-self-center tlw-px-6 tlw-py-3'
                        onClick={onCreateQuidTopicClick}
                        disabled={!selectedTopicId || validationResult.error}
                    >
                        Save
                    </RIQButton>
                </div>
                {validationResult.error && saveButtonTooltipNode}
            </div>

        </RIQModal>
    );
}

function QuidTopicSelector({title, description, children}) {
    return (
        <div
            className='tlw-mx-8 tlw-mb-12 tlw-grid tlw-grid-cols-9 tlw-gap-2'
        >
            <div
                className='tlw-col-start-2 tlw-col-end-2 tlw-mb-2.5 tlw-text-gray-900'
            >
                {title}
                <RIQInfoPopover
                    description={description}
                />
            </div>
            <div
                className='tlw-col-start-3 tlw-col-end-9 tlw-flex tlw-flex-col'
            >
                {children}
            </div>
        </div>
    );
}
